import { Rocket, Zap, Plus, TrendingUp, Calendar, Upload, CheckCircle2 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const timelineIcons = [Calendar, Upload, CheckCircle2] as const;
const featureIcons = [Rocket, Zap, Plus, TrendingUp] as const;

const SchnellEingefuehrtSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tArray, tObject } = useLanguage();
  const timeline = tObject<Array<{ week: string; title: string; description: string }>>("knowledgeBasePage.schnell.timeline");
  const features = tArray("knowledgeBasePage.schnell.features");

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              {t("knowledgeBasePage.schnell.badge")}
            </Badge>

            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              <span className="text-foreground">{t("knowledgeBasePage.schnell.titleLine1")}</span>
              <br />
              <span className="text-foreground">{t("knowledgeBasePage.schnell.titleLine2")}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {timeline.map((step, index) => {
              const Icon = timelineIcons[index];
              return (
                <div
                  key={step.week}
                  className={cn(
                    "relative bg-card rounded-xl p-6 border border-border transition-all text-center",
                    isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                  )}
                  style={{ transitionDelay: isVisible ? `${index * 150}ms` : "0ms", transitionDuration: "500ms" }}
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-chathero-mint text-primary px-3 py-0.5 rounded-full text-xs font-bold">
                    {step.week}
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-chathero-mint/20 flex items-center justify-center mx-auto mb-4 mt-2">
                    <Icon className="w-6 h-6 text-chathero-mint" />
                  </div>
                  <h3 className="font-semibold text-primary mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>

                  {index < timeline.length - 1 && <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-chathero-mint/30" />}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-8">
            {features.map((feature, index) => {
              const Icon = featureIcons[index];
              return (
                <div key={feature} className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-chathero-mint" />
                  <span className="text-sm font-medium text-foreground">{feature}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SchnellEingefuehrtSection;
